/**
 * Currency/region trust rules.
 *
 * Pure functions, no storage and no network, so the decision that governs
 * which currency a visitor is quoted can be tested directly.
 *
 * Background: currency is a pure function of the resolved country
 * (`currencyForCountry`), and the country ladder in country.js ends in a
 * GeoIP lookup. A GeoIP misread — VPN, corporate proxy, carrier CGNAT —
 * therefore silently reprices the whole site. On the Dutch storefront that
 * surfaced as "vanaf US$ 14,00": GeoIP said US, so the page switched to USD
 * against a US price that is a 1:1 copy of the euro price.
 */

// The only two markets that are not priced in euros.
const COUNTRY_CURRENCY = {
  GB: 'GBP',
  US: 'USD',
};

export const currencyForCountry = (countryCode) => COUNTRY_CURRENCY[countryCode] || 'EUR';

/**
 * Display symbol for a country's currency.
 *
 * A symbol swap is the whole job: the product catalog charges the same number
 * of minor units in every currency (the Large book is 3200 in EU, GB and US
 * alike), so nothing is converted — €32 and $32 are the same listed price.
 * Unknown/absent countries land on EUR, matching currencyForCountry.
 */
const CURRENCY_SYMBOL = { EUR: '€', GBP: '£', USD: '$' };

export const symbolForCountry = (countryCode) => CURRENCY_SYMBOL[currencyForCountry(countryCode)];

const LANG_LOCALE = { en: 'en-GB', nl: 'nl-NL', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' };

/**
 * Format minor units in the currency the visitor is charged in.
 *
 * For amounts the site computes itself — coupon and discount badges — as
 * opposed to amounts the products API has already formatted. It lives here so
 * the currency comes from the same place the prices next to it do: the badge
 * on the plan page used to hardcode EUR, so a US visitor read "€5.00 off" over
 * a plan quoted in dollars (KL-15620).
 */
export const formatAmountForCountry = (cents, countryCode, lang) =>
  new Intl.NumberFormat(LANG_LOCALE[lang] || LANG_LOCALE.en, {
    style: 'currency',
    currency: currencyForCountry(countryCode),
  }).format(cents / 100);

/**
 * IANA zone -> country, for the markets in active_countries.json only.
 *
 * Written against the zone string the browser actually reports, not the
 * canonical zone: engines disagree about which links they collapse. Verified
 * against tzdata 2025b — every zone below round-trips to itself except
 * `Europe/Nicosia`, which canonicalises to `Asia/Nicosia`, so both spellings
 * are listed. Zones outside our markets are deliberately absent; an unknown
 * zone means "no opinion", which is a safer answer than a wrong country.
 */
const TIMEZONE_COUNTRY = {
  'Europe/Vienna': 'AT',
  'Europe/Brussels': 'BE',
  'Asia/Nicosia': 'CY',
  'Europe/Nicosia': 'CY',
  'Europe/Prague': 'CZ',
  'Europe/Berlin': 'DE',
  'Europe/Busingen': 'DE',
  'Europe/Copenhagen': 'DK',
  'Europe/Tallinn': 'EE',
  'Europe/Madrid': 'ES',
  'Africa/Ceuta': 'ES',
  'Atlantic/Canary': 'ES',
  'Europe/Helsinki': 'FI',
  'Europe/Paris': 'FR',
  'Europe/Athens': 'GR',
  'Europe/Zagreb': 'HR',
  'Europe/Budapest': 'HU',
  'Europe/Dublin': 'IE',
  'Europe/Rome': 'IT',
  'Europe/Vilnius': 'LT',
  'Europe/Luxembourg': 'LU',
  'Europe/Riga': 'LV',
  'Europe/Malta': 'MT',
  'Europe/Amsterdam': 'NL',
  'Europe/Warsaw': 'PL',
  'Europe/Lisbon': 'PT',
  'Atlantic/Madeira': 'PT',
  'Atlantic/Azores': 'PT',
  'Europe/Stockholm': 'SE',
  'Europe/Ljubljana': 'SI',
  'Europe/Bratislava': 'SK',
};

export const countryFromTimezone = (timeZone) => TIMEZONE_COUNTRY[timeZone] || null;

/**
 * Zones that corroborate a non-euro detection.
 *
 * US is not a bare `America/*` prefix: Hawaii and the Pacific territories sit
 * outside that tree. Listed explicitly rather than by prefix so the set says
 * what it means.
 */
const US_ZONE_PREFIXES = ['America/'];
const US_ZONES = ['Pacific/Honolulu', 'Pacific/Guam', 'Pacific/Saipan', 'Pacific/Pago_Pago'];

// GB likewise cannot be a `Europe/*` prefix — Europe/Dublin is in that tree and
// is a euro market. Belfast reports Europe/London so Northern Ireland needs no
// entry; the Crown Dependencies do, because engines do not collapse those links.
const GB_ZONES = [
  'Europe/London',
  'Europe/Belfast',
  'Europe/Guernsey',
  'Europe/Isle_of_Man',
  'Europe/Jersey',
];

const isUsZone = (timeZone) =>
  US_ZONES.includes(timeZone) || US_ZONE_PREFIXES.some((p) => timeZone?.startsWith(p));

/**
 * Whether the browser's timezone corroborates a non-euro country.
 * Anything with no opinion (unknown or missing zone) does not corroborate —
 * silence is not agreement.
 */
export const timezoneMatchesCountry = (countryCode, timeZone) => {
  if (!timeZone) return false;
  if (countryCode === 'US') return isUsZone(timeZone);
  if (countryCode === 'GB') return GB_ZONES.includes(timeZone);
  return countryFromTimezone(timeZone) === countryCode;
};

/**
 * Decide whether a *detected* country can be trusted, and what to use when it
 * cannot. Explicit choices (modal pick, logged-in profile) never come through
 * here — they always win.
 *
 * @param {object} params
 * @param {string|null} params.detected  country code from GeoIP
 * @param {string} params.lang           storefront language ('en' | 'nl' | …)
 * @param {string} params.timeZone       IANA zone from the browser
 * @returns {string|null} country to use, or null when we cannot tell
 */
/**
 * Whether the clock positively places the visitor in a market we serve that is
 * not priced in euros. Used to challenge a euro detection, so it has to be a
 * positive identification: an unknown zone has no opinion.
 */
const isNonEuroZone = (timeZone) =>
  timezoneMatchesCountry('US', timeZone) || timezoneMatchesCountry('GB', timeZone);

export const resolveTrustedCountry = ({ detected, lang, timeZone }) => {
  if (currencyForCountry(detected) === 'EUR') {
    // A euro detection used to be waved through with no corroboration at all,
    // which is how a shared `?country=NL` link — this module writes that param,
    // so it rides along in any copied URL — or a stale storage value quoted a
    // New York visitor in euros and never offered them the modal (KL-15620).
    // Corroboration now cuts both ways, held to the same standard: only a
    // positive contradiction counts. Another euro market is not one (same
    // currency either way), and neither is silence. Euro storefronts are
    // exempt for the same reason they can't quote USD — they're euro by
    // definition, so a US clock there is no reason to doubt a euro country.
    if (lang === 'en' && isNonEuroZone(timeZone)) return null;
    return detected;
  }

  // Only the English storefront sells in USD/GBP, and only when the device
  // clock backs up the IP. Every other storefront is euro by definition, so
  // no amount of corroboration makes a non-euro detection right there.
  if (lang === 'en' && timezoneMatchesCountry(detected, timeZone)) return detected;

  // The detection would move the visitor off EUR and nothing corroborates it.
  // The timezone's own country is the better guess — but only when it is one
  // of ours and itself priced in euros, so this can never smuggle the
  // uncorroborated currency back in through the fallback.
  const fromZone = countryFromTimezone(timeZone);
  if (fromZone && currencyForCountry(fromZone) === 'EUR') return fromZone;
  return null;
};
